import { sendRequest } from '@infrastructure/api/api';
import { ChurchListResponse, ChurchMeta, ChurchQueryParams, Church } from '../types/church';
import ApiResponse from '@core/types/apiResponse';
import { mapPaginatedToSingle } from '@core/utils/mapPaginatedToSingle';
import { ReportTemplateAssignmentPayload, SyncReportTemplatesOptions } from '@features/report/template/types/reportTemplateAssignment';

/** Page size when loading every church in a district for pickers (no `all=true`). */
export const CHURCH_DISTRICT_PICKER_PAGE_SIZE = 100;

/**
 * Fetches a list of churches based on query parameters 
 * @param params - Query parameters for filtering and pagination
 * @returns Promise<ApiResponse<ChurchListResponse>>
 */
export const fetchChurches = async (params: ChurchQueryParams = {}): Promise<ApiResponse<ChurchListResponse>> => {
  const queryString = new URLSearchParams();
  
  // Add parameters to query string
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryString.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    }
  });

  return await sendRequest<any, ChurchListResponse>('GET', `church/query?${queryString}`);
};

/**
 * Loads every church in a district for head-church pickers.
 * Uses `districtId` only (never `all=true`, which would skip the district filter).
 */
export const fetchAllChurchesForDistrict = async (
  districtId: number
): Promise<ApiResponse<ChurchListResponse>> => {
  const firstResponse = await fetchChurches({
    districtId,
    page: 1,
    perPage: CHURCH_DISTRICT_PICKER_PAGE_SIZE,
  });
  if (!firstResponse.isSuccess()) {
    return firstResponse;
  }

  const firstPayload = firstResponse.getData();
  const churches: Church[] = [...(firstPayload?.data ?? [])];
  const lastPage = firstPayload?.meta?.last_page ?? 1;

  if (lastPage > 1) {
    const rest = await Promise.all(
      Array.from({ length: lastPage - 1 }, (_, index) =>
        fetchChurches({
          districtId,
          page: index + 2,
          perPage: CHURCH_DISTRICT_PICKER_PAGE_SIZE,
        })
      )
    );
    for (const pageResponse of rest) {
      if (!pageResponse.isSuccess()) {
        return pageResponse;
      }
      churches.push(...(pageResponse.getData()?.data ?? []));
    }
  }

  const baseMeta = (firstPayload?.meta ?? {}) as Partial<ChurchMeta>;
  const merged: ChurchListResponse = {
    data: churches,
    meta: {
      total: churches.length,
      per_page: churches.length || CHURCH_DISTRICT_PICKER_PAGE_SIZE,
      current_page: 1,
      last_page: 1,
      from: churches.length ? 1 : 0,
      to: churches.length,
      first_page_url: baseMeta.first_page_url ?? '',
      last_page_url: baseMeta.last_page_url ?? '',
      next_page_url: null,
      prev_page_url: null,
      path: baseMeta.path ?? '',
      has_more_pages: false,
      links: baseMeta.links ?? [],
    },
  };

  return {
    data: merged,
    error: null,
    status: firstResponse.status,
    isSuccess: () => true,
    isError: () => false,
    getData: () => merged,
    getError: () => null,
  };
};

/**
 * Fetches a single church by ID
 * @param id - Church ID
 * @returns Promise<ApiResponse<Church>>
 */
export const fetchChurchById = async (id: number): Promise<ApiResponse<Church>> => {
  const listResponse = await fetchChurches({ churchId: id, page: 1, perPage: 1 });
  return mapPaginatedToSingle<Church>(listResponse, 'Church not found');
};

/**
 * Creates a new church in a district
 * @param districtId - District ID where the church will be created
 * @param churchData - Church data for creation
 * @returns Promise<ApiResponse<Church>>
 */
export const createChurch = async (districtId: number, churchData: { name: string; description: string; alias?: string; established_at?: string }): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>('POST', `district/${districtId}/church/create`, churchData);
};

/**
 * Updates an existing church
 * @param districtId - District ID where the church belongs
 * @param churchId - Church ID to update
 * @param churchData - Updated church data
 * @returns Promise<ApiResponse<Church>>
 */
export const updateChurch = async (districtId: number, churchId: number, churchData: { name: string; description: string; alias?: string; established_at?: string }): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>('POST', `district/${districtId}/church/${churchId}/update`, churchData);
};

/** Disables a church (sets it inactive). Nation-admin endpoint. */
export const disableChurch = async (nationId: number, churchId: number): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<never, { message: string }>('POST', `nation/${nationId}/church/${churchId}/disable`);
};

/** Enables a church (sets it active). Nation-admin endpoint. */
export const enableChurch = async (nationId: number, churchId: number): Promise<ApiResponse<{ message: string }>> => {
  return await sendRequest<never, { message: string }>('POST', `nation/${nationId}/church/${churchId}/enable`);
};

/**
 * Syncs report template assignments to a church (atomic upsert + delete detached).
 */
export const syncTemplatesToChurch = async (
  districtId: number,
  churchId: number,
  departmentId: number,
  templates: ReportTemplateAssignmentPayload[],
  options?: SyncReportTemplatesOptions
): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>(
    'POST',
    `district/${districtId}/church/${churchId}/department/${departmentId}/reporttemplate/sync`,
    {
      department_id: departmentId,
      report_templates: templates,
      ...(options?.confirmClearAll ? { confirm_clear_all: true } : {}),
    }
  );
};

/**
 * Syncs report template assignments to a district (atomic upsert + delete detached).
 */
export const syncTemplatesToDistrict = async (
  districtId: number,
  departmentId: number,
  templates: ReportTemplateAssignmentPayload[],
  options?: SyncReportTemplatesOptions
): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>(
    'POST',
    `district/${districtId}/reporttemplate/sync`,
    {
      department_id: departmentId,
      report_templates: templates,
      ...(options?.confirmClearAll ? { confirm_clear_all: true } : {}),
    }
  );
};

/**
 * Assigns report templates to a church
 * @param districtId - District ID where the church belongs
 * @param churchId - Church ID to assign templates to
 * @param departmentId - Department ID for the templates
 * @param templates - Array of template assignment objects
 * @returns Promise<ApiResponse<any>>
 */
export const assignTemplatesToChurch = async (
  districtId: number,
  churchId: number,
  departmentId: number,
  templates: { report_template_id: number; list_order: number; is_required: boolean; required_at?: string | null }[]
): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>(
    'POST',
    `district/${districtId}/church/${churchId}/department/${departmentId}/reporttemplate/assign`,
    { report_templates: templates }
  );
};

/**
 * Fetches assigned report templates for a church department
 * @param churchId - Church ID to get templates for
 * @param departmentId - Department ID
 * @returns Promise<ApiResponse<any>>
 */
export const fetchChurchTemplates = async (churchId: number, departmentId: number): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>('GET', `church/${churchId}/department/${departmentId}/reporttemplate/query?all=true`);
};

export type ChurchAssignedTemplatesQueryParams = {
  all?: boolean;
  freetext?: string;
  page?: number;
  perPage?: number;
  reportTemplateName?: string;
  reportTemplateId?: number;
  reportTemplateDescription?: string;
  isRequired?: boolean;
  requiredDate?: string;
  isActive?: boolean;
};

/**
 * Fetches assigned report templates for a church across all departments.
 */
export const fetchChurchAssignedTemplates = async (
  churchId: number,
  params: ChurchAssignedTemplatesQueryParams = {}
): Promise<ApiResponse<any>> => {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryString.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    }
  });
  const qs = queryString.toString();
  return await sendRequest<any, any>(
    'GET',
    `church/${churchId}/reporttemplate/query${qs ? `?${qs}` : ''}`
  );
};

/**
 * Removes report template assignments from a church
 * @param districtId - District ID where the church belongs
 * @param churchId - Church ID to remove templates from
 * @param departmentId - Department ID for the templates
 * @param templateIds - Array of report template IDs to remove
 * @returns Promise<ApiResponse<any>>
 */
export const removeTemplatesFromChurch = async (
  districtId: number,
  churchId: number,
  departmentId: number,
  templateIds: number[]
): Promise<ApiResponse<any>> => {
  return await sendRequest<any, any>(
    'POST',
    `district/${districtId}/church/${churchId}/department/${departmentId}/reporttemplate/remove`,
    { report_template_ids: templateIds }
  );
};

/**
 * Fetches assigned report templates for a district across all departments.
 */
export const fetchDistrictAssignedTemplates = async (
  districtId: number,
  params: ChurchAssignedTemplatesQueryParams = {}
): Promise<ApiResponse<any>> => {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryString.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    }
  });
  const qs = queryString.toString();
  return await sendRequest<any, any>(
    'GET',
    `district/${districtId}/reporttemplate/query${qs ? `?${qs}` : ''}`
  );
};

/**
 * Assigns report templates to a district (single-entity, not bulk).
 */
export const assignTemplatesToDistrict = async (
  districtId: number,
  departmentId: number,
  templates: ReportTemplateAssignmentPayload[]
): Promise<ApiResponse<any>> => {
  return await sendRequest<
    { report_templates: ReportTemplateAssignmentPayload[]; department_id: number },
    any
  >('POST', `district/${districtId}/reporttemplate/assign`, {
    report_templates: templates,
    department_id: departmentId,
  });
};

/**
 * Removes report templates from a district (single-entity, not bulk).
 */
export const removeTemplatesFromDistrict = async (
  districtId: number,
  reportTemplateIds: number[]
): Promise<ApiResponse<any>> => {
  return await sendRequest<{ report_template_ids: number[] }, any>(
    'POST',
    `district/${districtId}/reporttemplate/remove`,
    { report_template_ids: reportTemplateIds }
  );
};

/**
 * Fetches assigned report templates for a nation across all departments.
 */
export const fetchNationAssignedTemplates = async (
  nationId: number,
  params: ChurchAssignedTemplatesQueryParams = {}
): Promise<ApiResponse<any>> => {
  const queryString = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryString.append(key, typeof value === 'boolean' ? (value ? '1' : '0') : String(value));
    }
  });
  const qs = queryString.toString();
  return await sendRequest<any, any>(
    'GET',
    `nation/${nationId}/reporttemplate/query${qs ? `?${qs}` : ''}`
  );
};

export type BulkTemplateResult = {
  assigned?: string[];
  updated?: string[];
  unassigned?: string[];
  skipped_already?: string[];
  skipped_inactive?: string[];
  skipped_not_assigned?: string[];
  failed?: string[];
  resolved_church_ids?: number[];
  resolved_district_ids?: number[];
};

/**
 * Bulk assigns report templates to multiple churches
 */
export const bulkAssignTemplatesToChurches = async (
  nationId: number,
  departmentId: number,
  churchIds: number[],
  templateIds: number[],
  options?: { isRequired?: boolean; requiredAt?: string | null; overrideExisting?: boolean }
): Promise<ApiResponse<BulkTemplateResult>> => {
  return await sendRequest<any, BulkTemplateResult>(
    'POST',
    `nation/${nationId}/reporttemplate/bulk/assign/church`,
    {
      church_ids: churchIds,
      report_template_ids: templateIds,
      department_id: departmentId,
      is_required: options?.isRequired ?? false,
      required_at: options?.requiredAt ?? undefined,
      override_existing: options?.overrideExisting ?? false,
    }
  );
};

/**
 * Bulk unassigns report templates from multiple churches
 */
export const bulkUnassignTemplatesFromChurches = async (
  nationId: number,
  churchIds: number[],
  templateIds: number[]
): Promise<ApiResponse<BulkTemplateResult>> => {
  return await sendRequest<any, BulkTemplateResult>(
    'POST',
    `nation/${nationId}/reporttemplate/bulk/unassign/church`,
    { church_ids: churchIds, report_template_ids: templateIds }
  );
};

/**
 * Bulk assigns report templates to multiple districts
 */
export const bulkAssignTemplatesToDistricts = async (
  nationId: number,
  departmentId: number,
  districtIds: number[],
  templateIds: number[],
  options?: { isRequired?: boolean; requiredAt?: string | null; overrideExisting?: boolean }
): Promise<ApiResponse<BulkTemplateResult>> => {
  return await sendRequest<any, BulkTemplateResult>(
    'POST',
    `nation/${nationId}/reporttemplate/bulk/assign/district`,
    {
      district_ids: districtIds,
      report_template_ids: templateIds,
      department_id: departmentId,
      is_required: options?.isRequired ?? false,
      required_at: options?.requiredAt ?? undefined,
      override_existing: options?.overrideExisting ?? false,
    }
  );
};

/**
 * Bulk unassigns report templates from multiple districts
 */
export const bulkUnassignTemplatesFromDistricts = async (
  nationId: number,
  districtIds: number[],
  templateIds: number[]
): Promise<ApiResponse<BulkTemplateResult>> => {
  return await sendRequest<any, BulkTemplateResult>(
    'POST',
    `nation/${nationId}/reporttemplate/bulk/unassign/district`,
    { district_ids: districtIds, report_template_ids: templateIds }
  );
};
